import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PromptSelect } from './PromptSelect';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCreateModel } from '@/hooks/useCreateModel';
import { FilterModelsDto, ModelService } from '@/services/ModelsService';

const schema = z.object({
  prompt: z.string().min(1, 'Prompt is required'),
  service: z.nativeEnum(ModelService, { message: 'Service is required' }),
  modelVersion: z.string().min(1, 'Model version is required'),
  customer: z.string().min(1, 'Customer is required'),
});

type FormData = z.infer<typeof schema>;

export function CreateModelForm({
  open,
  onClose,
  onSuccess,
  filter,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  filter: FilterModelsDto;
}) {
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: '',
      service: ModelService.CREATE_NAME,
      modelVersion: '',
      customer: '',
    },
  });

  const { createModel, isLoading } = useCreateModel(filter);
  const selectedCustomerId = Number(watch('customer'));
  const promptCustomerId =
    Number.isFinite(selectedCustomerId) && selectedCustomerId > 0
      ? selectedCustomerId
      : undefined;

  const onSubmit = handleSubmit(async (data: FormData) => {
    try {
      await createModel({
        service: data.service,
        modelVersion: data.modelVersion,
        promptId: parseInt(data.prompt, 10),
        customerId: parseInt(data.customer, 10),
      });
      toast.success('Model created successfully');
      reset();
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error('Failed to create model');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onClose} modal={false}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Model</DialogTitle>
          <DialogDescription>
            Define service, version and prompt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <FormGroup error={{ message: errors.prompt?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="prompt">Prompt</RequiredLabel>
            <Controller
              name="prompt"
              control={control}
              render={({ field }) => (
                <PromptSelect
                  value={field.value}
                  customerId={promptCustomerId}
                  onChange={(val) => {
                    field.onChange(String(val));
                  }}
                />
              )}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.service?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="service">Service</RequiredLabel>

            <Controller
              name="service"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Service" />
                  </SelectTrigger>

                  <SelectContent>
                    {Object.values(ModelService).map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          <FormGroup
            error={{ message: errors.modelVersion?.message, font: 'sm' }}
          >
            <RequiredLabel htmlFor="modelVersion">Model Version</RequiredLabel>
            <Input id="modelVersion" {...register('modelVersion')} />
          </FormGroup>

          <FormGroup error={{ message: errors.customer?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="customer">Customer</RequiredLabel>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <CustomerSelect
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                />
              )}
            />
          </FormGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting || isLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
